import type { DateTimeType, DateType, DateTimeUnixType } from '@/js/libs/api-helpers';
import * as Permissions from '@/js/libs/generated/permissions/types';
import * as Types from './types';

export enum AccessLevel {
  CREATE = 'CREATE',
  READ_ONLY = 'READ_ONLY',
}
export enum PermissionType {
  ANONYMOUS = 'ANONYMOUS',
  AGENCY = 'AGENCY',
  PUBLISHER = 'PUBLISHER',
  ACCESS_COMPLIANCE = 'ACCESS_COMPLIANCE',
  ADMIN_INVOICE = 'ADMIN_INVOICE',
  ANALYTICS_TOOL = 'ANALYTICS_TOOL',
  ADSERVER_CREATIVES = 'ADSERVER_CREATIVES',
  BOOK_CAMPAIGNS = 'BOOK_CAMPAIGNS',
  VIEW_CAMPAIGNS = 'VIEW_CAMPAIGNS',
  CONFIGURE_ADS_TXT = 'CONFIGURE_ADS_TXT',
  CREATIVES_CRAWLER = 'CREATIVES_CRAWLER',
  CREDIT_CARD_PAYMENT_ONLY = 'CREDIT_CARD_PAYMENT_ONLY',
  EXCLUDE_CREATIVE_APPROVAL = 'EXCLUDE_CREATIVE_APPROVAL',
  UPDATE_PERMITTED_DIGITAL_CREATIVES = 'UPDATE_PERMITTED_DIGITAL_CREATIVES',
  HEADER_TAG = 'HEADER_TAG',
  MANAGE_ACCOUNTS = 'MANAGE_ACCOUNTS',
  MANAGE_CAMPAIGN_TYPES = 'MANAGE_CAMPAIGN_TYPES',
  MANAGE_DIGITAL_CREATIVES = 'MANAGE_DIGITAL_CREATIVES',
  MANAGE_DIGITAL_PRODUCTS = 'MANAGE_DIGITAL_PRODUCTS',
  MANAGE_POS_PRODUCTS = 'MANAGE_POS_PRODUCTS',
  MANAGE_PRINT_CREATIVES = 'MANAGE_PRINT_CREATIVES',
  MANAGE_PRINT_PRODUCTS = 'MANAGE_PRINT_PRODUCTS',
  MANAGE_RADIO_PRODUCTS = 'MANAGE_RADIO_PRODUCTS',
  MANAGE_TV_PRODUCTS = 'MANAGE_TV_PRODUCTS',
  MANAGE_CONSULTING_PRODUCTS = 'MANAGE_CONSULTING_PRODUCTS',
  SYNC_DIGITAL_PRODUCTS = 'SYNC_DIGITAL_PRODUCTS',
  EARLY_PAYMENT_COMMISSION = 'EARLY_PAYMENT_COMMISSION',
  OPS_CHECKS = 'OPS_CHECKS',
  OPS_PUSH = 'OPS_PUSH',
  OPS_REPORTING = 'OPS_REPORTING',
  OPS_SCREENSHOTS = 'OPS_SCREENSHOTS',
  POSTADMANAGER = 'POSTADMANAGER',
  POSTAL_INVOICES = 'POSTAL_INVOICES',
  POSTNETZ = 'POSTNETZ',
  POSTNETZ_BRANCH_USER = 'POSTNETZ_BRANCH_USER',
  READ_ONLY_POSTNETZ_BRANCH_USER = 'READ_ONLY_POSTNETZ_BRANCH_USER',
  PRINT_OPS_PUSH = 'PRINT_OPS_PUSH',
  PAYOUT = 'PAYOUT',
  PAYMENTS = 'PAYMENTS',
  REPORT_BUILDER = 'REPORT_BUILDER',
  SALESFUNNEL = 'SALESFUNNEL',
  SELF_BOOKING = 'SELF_BOOKING',
  YALEO_SELF_BOOKING = 'YALEO_SELF_BOOKING',
  SEMANTIQ = 'SEMANTIQ',
  TRAFFIC_DASHBOARD_ACCESS = 'TRAFFIC_DASHBOARD_ACCESS',
  YALEO = 'YALEO',
  MANAGE_COOP_WEEK = 'MANAGE_COOP_WEEK',
  SWITCH_COMPANY_VIEW = 'SWITCH_COMPANY_VIEW',
  SUPPORT_CENTER = 'SUPPORT_CENTER',
  MANAGE_REFERENCE_PER_AD_MEDIA = 'MANAGE_REFERENCE_PER_AD_MEDIA',
  SSO = 'SSO',
  STAGE_PERFORMANCE = 'STAGE_PERFORMANCE',
  TARGET_ALL_WEBSITES = 'TARGET_ALL_WEBSITES',
  SPECIAL_DISCOUNTS = 'SPECIAL_DISCOUNTS',
  XANDR_SEGMENTS = 'XANDR_SEGMENTS',
  ADDITIONAL_TAX = 'ADDITIONAL_TAX',
  EMAIL_SYNC = 'EMAIL_SYNC',
  BTA_FEED_REPORT_BUILDER = 'BTA_FEED_REPORT_BUILDER',
  LOCKED_FIELDS = 'LOCKED_FIELDS',
  GEN_AI = 'GEN_AI',
  PROGRAMMATIC_DEALS = 'PROGRAMMATIC_DEALS',
}
export enum EntityType {
  COMPANY = 'COMPANY',
  USER = 'USER',
  CAMPAIGN_TYPE = 'CAMPAIGN_TYPE',
  ACCOUNT = 'ACCOUNT',
  INDUSTRY = 'INDUSTRY',
  CONTACT = 'CONTACT',
  WEBSITE = 'WEBSITE',
  CAMPAIGN = 'CAMPAIGN',
  POST_CAMPAIGN = 'POST_CAMPAIGN',
  POST_ACCOUNT = 'POST_ACCOUNT',
  POST_CONTACT = 'POST_CONTACT',
  POST_INDUSTRY = 'POST_INDUSTRY',
  PRICE_LIST = 'PRICE_LIST',
  CAMPAIGN_SUB_GROUP = 'CAMPAIGN_SUB_GROUP',
  POSITION_SUB_GROUP = 'POSITION_SUB_GROUP',
  CAMPAIGN_POSITION = 'CAMPAIGN_POSITION',
  DIGITAL_CAMPAIGN_POSITION = 'DIGITAL_CAMPAIGN_POSITION',
  DIGITAL_AD_MEDIUM = 'DIGITAL_AD_MEDIUM',
  DIGITAL_AD_MEDIUM_MAPPING = 'DIGITAL_AD_MEDIUM_MAPPING',
  DIGITAL_PUBLISHER_SHARE = 'DIGITAL_PUBLISHER_SHARE',
  DIGITAL_AD_PRICE = 'DIGITAL_AD_PRICE',
  DIGITAL_AD_SPEC = 'DIGITAL_AD_SPEC',
  DIGITAL_AD_TYPE = 'DIGITAL_AD_TYPE',
  DIGITAL_PLACEMENT = 'DIGITAL_PLACEMENT',
  DIGITAL_CAMPAIGN_SUB_GROUP = 'DIGITAL_CAMPAIGN_SUB_GROUP',
  EXTERNAL_IDENTIFIER_TYPE = 'EXTERNAL_IDENTIFIER_TYPE',
  PRINT_CAMPAIGN_POSITION = 'PRINT_CAMPAIGN_POSITION',
  PRINT_AD_MEDIUM = 'PRINT_AD_MEDIUM',
  PRINT_AD_MEDIUM_MAPPING = 'PRINT_AD_MEDIUM_MAPPING',
  PRINT_AD_PRICE = 'PRINT_AD_PRICE',
  PRINT_AD_SPEC = 'PRINT_AD_SPEC',
  PRINT_AD_TYPE = 'PRINT_AD_TYPE',
  PRINT_PLACEMENT = 'PRINT_PLACEMENT',
  PRINT_CAMPAIGN_SUB_GROUP = 'PRINT_CAMPAIGN_SUB_GROUP',
  BROADCAST_CAMPAIGN_POSITION = 'BROADCAST_CAMPAIGN_POSITION',
  BROADCAST_AD_MEDIUM = 'BROADCAST_AD_MEDIUM',
  BROADCAST_AD_PRICE = 'BROADCAST_AD_PRICE',
  BROADCAST_AD_SPEC = 'BROADCAST_AD_SPEC',
  BROADCAST_AD_TYPE = 'BROADCAST_AD_TYPE',
  BROADCAST_PLACEMENT = 'BROADCAST_PLACEMENT',
  POINT_OF_SALE_CAMPAIGN_POSITION = 'POINT_OF_SALE_CAMPAIGN_POSITION',
  POINT_OF_SALE_AD_MEDIUM = 'POINT_OF_SALE_AD_MEDIUM',
  POINT_OF_SALE_AD_PRICE = 'POINT_OF_SALE_AD_PRICE',
  POINT_OF_SALE_AD_SPEC = 'POINT_OF_SALE_AD_SPEC',
  POINT_OF_SALE_AD_TYPE = 'POINT_OF_SALE_AD_TYPE',
  POINT_OF_SALE_CAMPAIGN_SUB_GROUP = 'POINT_OF_SALE_CAMPAIGN_SUB_GROUP',
  POINT_OF_SALE_PLACEMENT = 'POINT_OF_SALE_PLACEMENT',
  CONSULTING_AD_MEDIUM = 'CONSULTING_AD_MEDIUM',
  CONSULTING_AD_PRICE = 'CONSULTING_AD_PRICE',
  CONSULTING_AD_SPEC = 'CONSULTING_AD_SPEC',
  CONSULTING_AD_TYPE = 'CONSULTING_AD_TYPE',
  CONSULTING_AD_PLACEMENT = 'CONSULTING_AD_PLACEMENT',
  CONSULTING_CAMPAIGN_POSITION = 'CONSULTING_CAMPAIGN_POSITION',
  CONSULTING_CAMPAIGN_SUB_GROUP = 'CONSULTING_CAMPAIGN_SUB_GROUP',
  AD_SERVER_TECH_FEE = 'AD_SERVER_TECH_FEE',
  DELIVERY_DATA = 'DELIVERY_DATA',
  FORECAST = 'FORECAST',
  CAMPAIGN_POSITION_REVENUE_BY_DAY = 'CAMPAIGN_POSITION_REVENUE_BY_DAY',
  CAMPAIGN_POSITION_REVENUE_BY_WEBSITE = 'CAMPAIGN_POSITION_REVENUE_BY_WEBSITE',
  CAMPAIGN_POSITION_REVENUE_BY_CREATIVE = 'CAMPAIGN_POSITION_REVENUE_BY_CREATIVE',
  CONVERSION_TRACKER = 'CONVERSION_TRACKER',
  PAYOUT = 'PAYOUT',
  PAYOUT_WEBSITE = 'PAYOUT_WEBSITE',
  PAYOUT_CONFIG = 'PAYOUT_CONFIG',
  PAYOUT_MODIFICATION = 'PAYOUT_MODIFICATION',
  PAYOUT_MONTH_STATUS = 'PAYOUT_MONTH_STATUS',
  PAYOUT_PER_CAMPAIGN_POSITION = 'PAYOUT_PER_CAMPAIGN_POSITION',
  WEBSITE_GROUP = 'WEBSITE_GROUP',
  COMPANY_STATISTICS = 'COMPANY_STATISTICS',
  POSITION_GROUP = 'POSITION_GROUP',
  POSITION_GROUP_ENTRY = 'POSITION_GROUP_ENTRY',
  PUBLICATION_CALENDAR = 'PUBLICATION_CALENDAR',
  SIZE_CONFIG = 'SIZE_CONFIG',
  AVAILABILITY = 'AVAILABILITY',
  ADVERTISEMENT = 'ADVERTISEMENT',
  ADVERTISER = 'ADVERTISER',
  ADVERTISING_SPACE = 'ADVERTISING_SPACE',
  BRANCH_CLUSTER = 'BRANCH_CLUSTER',
  HISTORY_LOG = 'HISTORY_LOG',
  LABEL = 'LABEL',
  LABEL_EXTRACTION = 'LABEL_EXTRACTION',
  DISPATCH = 'DISPATCH',
  ADDRESSED_MAILING_POSITION = 'ADDRESSED_MAILING_POSITION',
  AD_UNIT = 'AD_UNIT',
  AD_UNIT_DETAILS = 'AD_UNIT_DETAILS',
  ADDRESSED_AD_UNIT_GROUP_DETAILS = 'ADDRESSED_AD_UNIT_GROUP_DETAILS',
  AUDIENCE_SEGMENT = 'AUDIENCE_SEGMENT',
  GOOGLE_LANGUAGE = 'GOOGLE_LANGUAGE',
  GOOGLE_LOCATION = 'GOOGLE_LOCATION',
  DIGITAL_POSITION = 'DIGITAL_POSITION',
  DISCOUNT_POSITION = 'DISCOUNT_POSITION',
  EMAIL_POSITION = 'EMAIL_POSITION',
  POST_INVOICE = 'POST_INVOICE',
  POST_INVOICE_POSITION = 'POST_INVOICE_POSITION',
  MAILING_POSITION = 'MAILING_POSITION',
  ONLINE_POSITION = 'ONLINE_POSITION',
  SERVICE_POSITION = 'SERVICE_POSITION',
  TARGET_GROUP = 'TARGET_GROUP',
  ADS_CONFIG = 'ADS_CONFIG',
  REPORTING_PAGE = 'REPORTING_PAGE',
  REPORTING_TEMPLATE = 'REPORTING_TEMPLATE',
  REPORTING_WIDGET = 'REPORTING_WIDGET',
  REPORTING_CAMPAIGN = 'REPORTING_CAMPAIGN',
  CAMPAIGN_REPORT = 'CAMPAIGN_REPORT',
  POSITION_REPORT = 'POSITION_REPORT',
  ACTIVITY = 'ACTIVITY',
  SELFBOOKED_CAMPAIGN_HISTORY = 'SELFBOOKED_CAMPAIGN_HISTORY',
  ACCOUNT_HISTORY = 'ACCOUNT_HISTORY',
  COMMENT = 'COMMENT',
  KEYWORD_GROUP_DEFAULTS = 'KEYWORD_GROUP_DEFAULTS',
  KEYWORD_GROUP_HISTORY = 'KEYWORD_GROUP_HISTORY',
  KEYWORD_GROUP = 'KEYWORD_GROUP',
  ACCOUNT_DIFF = 'ACCOUNT_DIFF',
  INVOICE_CHARGE = 'INVOICE_CHARGE',
  POSITION_DIFF = 'POSITION_DIFF',
  PRICE_DIFF = 'PRICE_DIFF',
  COOP_WEEK = 'COOP_WEEK',
  COMPANY_CONFIG = 'COMPANY_CONFIG',
  CREDIT_CARD = 'CREDIT_CARD',
  STRIPE_PAYMENT = 'STRIPE_PAYMENT',
  STRIPE_PAYOUT = 'STRIPE_PAYOUT',
  ARTICLE = 'ARTICLE',
  CRAWLER_CONFIG = 'CRAWLER_CONFIG',
  CRAWLER_PATH_CONFIG = 'CRAWLER_PATH_CONFIG',
  CRAWLER_PATH_POSITION = 'CRAWLER_PATH_POSITION',
  CRAWLER_PATH_TEMPLATE = 'CRAWLER_PATH_TEMPLATE',
  ACCOUNT_EMAIL = 'ACCOUNT_EMAIL',
  ACCOUNT_EMAIL_ATTACHMENT = 'ACCOUNT_EMAIL_ATTACHMENT',
  ACCOUNT_TYPE = 'ACCOUNT_TYPE',
  SYNCHRONIZED_USER = 'SYNCHRONIZED_USER',
  COMPANY_SYNC_SETTINGS = 'COMPANY_SYNC_SETTINGS',
  STOCK_ENTRY = 'STOCK_ENTRY',
  IMPORTS = 'IMPORTS',
  PRINT_CREATIVE = 'PRINT_CREATIVE',
  PRINT_CAMPAIGN_POSITION_EXPORT = 'PRINT_CAMPAIGN_POSITION_EXPORT',
  PUBLICATION = 'PUBLICATION',
  SCREENSHOT = 'SCREENSHOT',
  SCREENSHOT_CAMPAIGN = 'SCREENSHOT_CAMPAIGN',
  SCREENSHOT_DIGITAL_CAMPAIGN_POSITION = 'SCREENSHOT_DIGITAL_CAMPAIGN_POSITION',
  DIGITAL_CREATIVE = 'DIGITAL_CREATIVE',
  DIGITAL_CREATIVE_UPLOAD = 'DIGITAL_CREATIVE_UPLOAD',
  FORM_TEMPLATE = 'FORM_TEMPLATE',
  FORM_TEMPLATE_LEAD = 'FORM_TEMPLATE_LEAD',
  MEETING = 'MEETING',
  PROMOTION_MAIL_TEMPLATE = 'PROMOTION_MAIL_TEMPLATE',
  SALES_PROMOTION = 'SALES_PROMOTION',
  SALES_PROMOTION_ACCOUNT = 'SALES_PROMOTION_ACCOUNT',
  SALES_PROMOTION_CONTACT = 'SALES_PROMOTION_CONTACT',
  SALES_PROMOTION_TYPE_CONFIG = 'SALES_PROMOTION_TYPE_CONFIG',
  SOURCE = 'SOURCE',
  TASK = 'TASK',
  TASK_TOPIC = 'TASK_TOPIC',
  TOPIC = 'TOPIC',
  TOPIC_DISCUSSION = 'TOPIC_DISCUSSION',
  DIGITAL_CAMPAIGN_POSITION_CHECK = 'DIGITAL_CAMPAIGN_POSITION_CHECK',
  WEBSITE_MAPPING = 'WEBSITE_MAPPING',
  POSTNETZ_DOCUMENT = 'POSTNETZ_DOCUMENT',
  COMMON_AD_MEDIUM = 'COMMON_AD_MEDIUM',
  COMMON_AD_TYPE = 'COMMON_AD_TYPE',
  COMMON_PLACEMENT = 'COMMON_PLACEMENT',
  DISCOUNT = 'DISCOUNT',
  DISCOUNT_DETAIL = 'DISCOUNT_DETAIL',
  INVOICE = 'INVOICE',
  INVOICE_COMPANY_CONFIG = 'INVOICE_COMPANY_CONFIG',
  INVOICE_POSITION = 'INVOICE_POSITION',
  INVOICE_PAYMENT = 'INVOICE_PAYMENT',
  PINGEN_LETTER = 'PINGEN_LETTER',
  AD_MANAGER_REVENUE = 'AD_MANAGER_REVENUE',
  BRAND_COMPANY_CONFIG = 'BRAND_COMPANY_CONFIG',
  DEAL_COMPANY_CONFIG = 'DEAL_COMPANY_CONFIG',
  SSP_REVENUE_CONFIG = 'SSP_REVENUE_CONFIG',
  SSP_CAMPAIGN = 'SSP_CAMPAIGN',
  SSP_CAMPAIGN_POSITION = 'SSP_CAMPAIGN_POSITION',
  SSP_DELIVERY_DATA_PER_DAY = 'SSP_DELIVERY_DATA_PER_DAY',
  AD_SENSE_TAG_TEMPLATE = 'AD_SENSE_TAG_TEMPLATE',
  AD_SENSE_TEMPLATE = 'AD_SENSE_TEMPLATE',
  AD_TAG = 'AD_TAG',
  PARAMETERS_MEDIATION = 'PARAMETERS_MEDIATION',
  PLACEMENT_MEDIATION = 'PLACEMENT_MEDIATION',
  AMAZON_TEMPLATE = 'AMAZON_TEMPLATE',
  C_WIRE_TEMPLATE = 'C_WIRE_TEMPLATE',
  DEPARTMENT_CONFIG = 'DEPARTMENT_CONFIG',
  DEPARTMENT_CONFIGS_SIZE_MAPPING = 'DEPARTMENT_CONFIGS_SIZE_MAPPING',
  DISABLE_RULE = 'DISABLE_RULE',
  DYNAMIC_PLACEMENT = 'DYNAMIC_PLACEMENT',
  IX_TEMPLATE = 'IX_TEMPLATE',
  HEADER_TAG_PLACEMENT = 'HEADER_TAG_PLACEMENT',
  RAW_SCRIPT = 'RAW_SCRIPT',
  SIZE = 'SIZE',
  SIZE_MAPPING = 'SIZE_MAPPING',
  TAG = 'TAG',
  APP_NEXUS_REVENUE = 'APP_NEXUS_REVENUE',
  XANDR_CREATIVE = 'XANDR_CREATIVE',
  GAM_CREATIVE = 'GAM_CREATIVE',
  DSP_DELIVERY_DATA = 'DSP_DELIVERY_DATA',
  DSP_DELIVERY_DATA_PER_CREATIVE = 'DSP_DELIVERY_DATA_PER_CREATIVE',
  DSP_DELIVERY_DATA_PER_SITE = 'DSP_DELIVERY_DATA_PER_SITE',
  DSP_DELIVERY_DATA_PER_SITE_PER_DAY = 'DSP_DELIVERY_DATA_PER_SITE_PER_DAY',
  DSP_DELIVERY_DATA_PER_SIZE = 'DSP_DELIVERY_DATA_PER_SIZE',
  LINE_ITEM = 'LINE_ITEM',
  PUBLISHER = 'PUBLISHER',
  BRAND_REVENUE = 'BRAND_REVENUE',
  DEAL_REVENUE = 'DEAL_REVENUE',
  DEAL = 'DEAL',
  DEAL_REPORT = 'DEAL_REPORT',
  DEAL_REPORT_AGGREGATED = 'DEAL_REPORT_AGGREGATED',
  LINE_ITEM_SERVICE_RESULT = 'LINE_ITEM_SERVICE_RESULT',
  TAG_DEPARTMENT_CONFIG = 'TAG_DEPARTMENT_CONFIG',
  TAG_PARAMETER = 'TAG_PARAMETER',
  TEADS_TEMPLATE = 'TEADS_TEMPLATE',
  XANDER_BANNER_TEMPLATE = 'XANDER_BANNER_TEMPLATE',
  AD_SENSE_BIDDER_CONFIG = 'AD_SENSE_BIDDER_CONFIG',
  AMAZON_BIDDER_CONFIG = 'AMAZON_BIDDER_CONFIG',
  BIDDER_CONFIG = 'BIDDER_CONFIG',
  DEFAULT_BIDDER_CONFIG = 'DEFAULT_BIDDER_CONFIG',
  FLOOR_RULE = 'FLOOR_RULE',
  FLOOR_RULE_ENTRY = 'FLOOR_RULE_ENTRY',
  GOOGLE_BIDDER_CONFIG = 'GOOGLE_BIDDER_CONFIG',
  HEADER_TAG = 'HEADER_TAG',
  HEADER_TAG_BIDDER = 'HEADER_TAG_BIDDER',
  HEADER_TAG_BIDDER_SUPPLY_CHAIN = 'HEADER_TAG_BIDDER_SUPPLY_CHAIN',
  HEADER_TAG_RAW_SCRIPT = 'HEADER_TAG_RAW_SCRIPT',
  HEADER_TAG_PLACEMENT_BIDDER = 'HEADER_TAG_PLACEMENT_BIDDER',
  SERVER_SIDE_BIDDER_CONFIG = 'SERVER_SIDE_BIDDER_CONFIG',
  SUPPLY_CHAIN = 'SUPPLY_CHAIN',
  SDK_GLOBAL_CONFIG = 'SDK_GLOBAL_CONFIG',
  SDK_AD_CONFIG = 'SDK_AD_CONFIG',
  SDK_PUBLISHER = 'SDK_PUBLISHER',
  TICKET = 'TICKET',
  TICKET_ACTIVITY = 'TICKET_ACTIVITY',
  INVOICE_INFO = 'INVOICE_INFO',
  COMPANY_MAIL_TEMPLATE = 'COMPANY_MAIL_TEMPLATE',
  EMAIL_DESIGN = 'EMAIL_DESIGN',
  COMPANY_PDF_FOOTER_TEMPLATE = 'COMPANY_PDF_FOOTER_TEMPLATE',
  PDF_STYLE = 'PDF_STYLE',
  ROW_CONFIG = 'ROW_CONFIG',
  PDF_TYPE_CONFIG = 'PDF_TYPE_CONFIG',
  ADS_BLOCK = 'ADS_BLOCK',
  ADS_ENTRY = 'ADS_ENTRY',
  REFERENCE_LIST = 'REFERENCE_LIST',
  POST_DOCUMENT_METADATA = 'POST_DOCUMENT_METADATA',
  COMPANY_NOTIFICATION = 'COMPANY_NOTIFICATION',
  ACCOUNTING_INFO = 'ACCOUNTING_INFO',
  COMPANY_DISCOUNT = 'COMPANY_DISCOUNT',
  PPT_STYLE = 'PPT_STYLE',
  PPT_COMMENT_TEMPLATE = 'PPT_COMMENT_TEMPLATE',
  CUSTOM_INVOICE_ID_RANGE = 'CUSTOM_INVOICE_ID_RANGE',
  SELFBOOKING_ADVERTISER = 'SELFBOOKING_ADVERTISER',
  BALANCE_RECORD = 'BALANCE_RECORD',
  SELFBOOKING_CAMPAIGN = 'SELFBOOKING_CAMPAIGN',
  SELFBOOKING_SSP_CAMPAIGN = 'SELFBOOKING_SSP_CAMPAIGN',
  SELFBOOKING_CAMPAIGN_POSITION = 'SELFBOOKING_CAMPAIGN_POSITION',
  SELFBOOKING_SSP_CAMPAIGN_POSITION = 'SELFBOOKING_SSP_CAMPAIGN_POSITION',
  SELFBOOKING_CONVERSION_TRACKER = 'SELFBOOKING_CONVERSION_TRACKER',
  SELFBOOKING_CREATIVE = 'SELFBOOKING_CREATIVE',
  SELFBOOKING_DELIVERY_DATA = 'SELFBOOKING_DELIVERY_DATA',
  SELFBOOKING_DELIVERY_DATA_PER_CREATIVE = 'SELFBOOKING_DELIVERY_DATA_PER_CREATIVE',
  SELFBOOKING_SSP_DELIVERY_DATA = 'SELFBOOKING_SSP_DELIVERY_DATA',
  SELFBOOKING_DOCUMENT = 'SELFBOOKING_DOCUMENT',
  SELFBOOKING_INVOICE = 'SELFBOOKING_INVOICE',
  SELFBOOKING_INVOICE_POSITION = 'SELFBOOKING_INVOICE_POSITION',
  SELFBOOKING_WEBSITE = 'SELFBOOKING_WEBSITE',
  SELFBOOKING_CAMPAIGN_POSITION_HISTORY = 'SELFBOOKING_CAMPAIGN_POSITION_HISTORY',
  SELFBOOKING_CAMPAIGN_POSITION_PRICE_HISTORY = 'SELFBOOKING_CAMPAIGN_POSITION_PRICE_HISTORY',
  SELFBOOKING_ACTIVITY = 'SELFBOOKING_ACTIVITY',
  CUSTOM_PAYOUT_ID_RANGE = 'CUSTOM_PAYOUT_ID_RANGE',
  TEMPORARY_ACCESS = 'TEMPORARY_ACCESS',
  LOCKED_FIELDS_CONFIG = 'LOCKED_FIELDS_CONFIG',
}
